/**
 * Extract text content from Contentful rich text document
 * @param {Object} richTextDoc - Contentful rich text document
 * @returns {string} - Extracted plain text
 */
const extractTextFromRichText = (richTextDoc) => {
  if (!richTextDoc || !richTextDoc.content) return '';
  
  let text = '';
  const traverse = (nodes) => {
    if (!Array.isArray(nodes)) return;
    
    nodes.forEach(node => {
      if (node.nodeType === 'text') {
        text += node.value + ' ';
      }
      if (node.content && Array.isArray(node.content)) {
        traverse(node.content);
      }
    });
  };
  
  traverse(richTextDoc.content);
  return text.trim();
};

/**
 * Search across all content sections
 * @param {string} searchTerm - The search query
 * @param {Object} contentData - All content data from Redux
 * @returns {Array|null} - Array of search results or null if no matches
 */
export const searchContent = (searchTerm, contentData) => {
  if (!searchTerm || !contentData) return null;
  
  const term = searchTerm.toLowerCase().trim();
  
  // Minimum search length
  if (term.length < 2) return null;
  
  const results = [];

  // 1. Search in Services
  if (contentData.Services && Array.isArray(contentData.Services)) {
    contentData.Services.forEach((service, index) => {
      const titleMatch = service.Title?.toLowerCase().includes(term);
      
      // Extract and search in description
      let descText = '';
      if (Array.isArray(service.Desc)) {
        descText = service.Desc.map(desc => extractTextFromRichText(desc)).join(' ');
      }
      const descMatch = descText.toLowerCase().includes(term);
      
      if (titleMatch || descMatch) {
        results.push({
          section: 'services',
          route: `/#services`,
          scrollTo: 'services',
          title: service.Title,
          type: 'Service',
          matchType: titleMatch ? 'title' : 'description',
          index,
          slug: service.Slug
        });
      }
    });
  }

  // 2. Search in Contact Copenhagen
  if (contentData.ContactCopenhagen) {
    const cph = contentData.ContactCopenhagen;
    const searchableFields = [
      cph.visitingAddress,
      cph.visitingAddressHeading,
      cph.openingHoursWeekdays,
      cph.openingHoursWeekends,
      cph.mailingAddress,
      cph.phoneContactsHeading,
      cph.accountingCustomsHeading,
      cph.phoneTitle,
      cph.mailHeading,
      cph.emailTitle,
      cph.mailingAddressHeading,
      cph.openingHoursHeading,
      cph.emailHeading,
      ...(cph.customerServiceEmails || []),
      ...(cph.accountingCustomsEmails?.map(e => e.email) || []),
      ...(cph.phoneContacts?.map(p => p.Number) || [])
    ];
    
    const cphText = searchableFields.filter(Boolean).join(' ').toLowerCase();
    
    if (cphText.includes(term) || 'copenhagen'.includes(term) || 'cph'.includes(term)) {
      results.push({
        section: 'contact',
        route: '/contact-us-cp',
        title: 'Contact Copenhagen',
        type: 'Contact Information',
        matchType: 'contact',
        location: 'Copenhagen'
      });
    }
  }

  // 3. Search in Contact Billund
  if (contentData.ContactBillund) {
    const bll = contentData.ContactBillund;
    const searchableFields = [
      bll.visitingAddress,
      bll.visitingAddressHeading,
      bll.openingHoursWeekdays,
      bll.openingHoursWeekends,
      bll.phoneContactsHeading,
      bll.accountingCustomsHeading,
      bll.phoneTitle,
      bll.emailTitle,
      bll.openingHoursHeading,
      bll.emailHeading,
      ...(bll.customerServiceEmails || []),
      ...(bll.accountingCustomsEmails || []),
      ...(bll.phoneContacts?.map(p => p.Phone) || [])
    ];
    
    const bllText = searchableFields.filter(Boolean).join(' ').toLowerCase();
    
    if (bllText.includes(term) || 'billund'.includes(term) || 'bll'.includes(term)) {
      results.push({
        section: 'contact',
        route: '/contact-us-bl',
        title: 'Contact Billund',
        type: 'Contact Information',
        matchType: 'contact',
        location: 'Billund'
      });
    }
  }

  // 4. Search in CPH Rates
  if (contentData.CphRates && Array.isArray(contentData.CphRates)) {
    contentData.CphRates.forEach((rate, index) => {
      const titleMatch = rate.Title?.toLowerCase().includes(term);
      
      if (titleMatch || 'copenhagen rates'.includes(term) || 'cph rates'.includes(term)) {
        results.push({
          section: 'rates',
          route: '/ratescph',
          title: `${rate.Title} (CPH)`,
          type: 'Copenhagen Rate',
          matchType: 'rate',
          index,
          location: 'Copenhagen',
          pdfTitle: rate.Title // Pass the PDF title for opening the correct PDF
        });
      }
    });
  }

  // 5. Search in Billund Rates
  if (contentData.BillundRates && Array.isArray(contentData.BillundRates)) {
    contentData.BillundRates.forEach((rate, index) => {
      const titleMatch = rate.Title?.toLowerCase().includes(term);
      
      if (titleMatch || 'billund rates'.includes(term) || 'bll rates'.includes(term)) {
        results.push({
          section: 'rates',
          route: '/ratesbll',
          title: `${rate.Title} (BLL)`,
          type: 'Billund Rate',
          matchType: 'rate',
          index,
          location: 'Billund',
          pdfTitle: rate.Title // Pass the PDF title for opening the correct PDF
        });
      }
    });
  }

  // 6. Search in FAQs
  if (contentData.Faq && Array.isArray(contentData.Faq)) {
    contentData.Faq.forEach((faq, index) => {
      const titleMatch = faq.Title?.toLowerCase().includes(term);
      const descText = extractTextFromRichText(faq.Desc);
      const descMatch = descText.toLowerCase().includes(term);
      
      if (titleMatch || descMatch) {
        results.push({
          section: 'faq',
          route: '/#faq',
          scrollTo: 'faq',
          title: faq.Title,
          type: 'FAQ',
          matchType: titleMatch ? 'question' : 'answer',
          index
        });
      }
    });
  }

  // 7. Search in Locations
  if (contentData.Location && Array.isArray(contentData.Location)) {
    contentData.Location.forEach((location, index) => {
      const titleMatch = location.fields?.pageTitle?.toLowerCase().includes(term);
      
      if (titleMatch) {
        const locationName = location.fields.pageTitle.toLowerCase();
        let route = '/#location-cards';
        
        if (locationName.includes('copenhagen')) {
          route = '/contact-us-cp';
        } else if (locationName.includes('billund')) {
          route = '/contact-us-bl';
        } else if (locationName.includes('track')) {
          route = '/#location-cards';
        }
        
        results.push({
          section: 'location',
          route,
          scrollTo: 'location-cards',
          title: location.fields.pageTitle,
          type: 'Location',
          matchType: 'location',
          index
        });
      }
    });
  }

  // 8. Search in About Us Banner
  if (contentData.AboutUsBanner) {
    const banner = contentData.AboutUsBanner;
    const titleMatch = banner.AboutUsTitle?.toLowerCase().includes(term);
    const contentText = extractTextFromRichText(banner.AboutUsContentText);
    const contentMatch = contentText.toLowerCase().includes(term);
    
    if (titleMatch || contentMatch) {
      results.push({
        section: 'about',
        route: '/about-us',
        title: 'About Us',
        type: 'About Us Banner',
        matchType: titleMatch ? 'title' : 'content'
      });
    }
  }

  // 9. Search in About Us Content
  if (contentData.AboutUsContent && Array.isArray(contentData.AboutUsContent)) {
    contentData.AboutUsContent.forEach((item, index) => {
      const titleMatch = item.Title?.toLowerCase().includes(term);
      const contentText = extractTextFromRichText(item.Content);
      const contentMatch = contentText.toLowerCase().includes(term);
      
      if (titleMatch || contentMatch) {
        results.push({
          section: 'about',
          route: '/about-us',
          title: item.Title,
          type: 'About Us Section',
          matchType: titleMatch ? 'title' : 'content',
          index
        });
      }
    });
  }

  // 10. Search in general terms (About Us, etc.)
  const generalTerms = [
    { keywords: ['about', 'about us', 'company'], route: '/about-us', title: 'About Us', type: 'Page' },
    { keywords: ['track', 'tracking', 'shipment', 'trace'], route: '/', title: 'Track Shipment', type: 'Feature'},
    { keywords: ['home', 'homepage', 'cargo', 'spirit', 'scandinavian'], route: '/', title: 'Home Page', type: 'Page' },
    { keywords: ['rates', 'pricing', 'price list', 'money'], route: '/ratescph', title: 'Rates & Pricing Copenhagen', type: 'Information' },
    { keywords: ['rates', 'pricing', 'price list', 'money'], route: '/ratesbll', title: 'Rates & Pricing Billund', type: 'Information' },
    { keywords: ['certificates', 'certification', 'iata', 'ceiv', 'pharma'], route: '/', title: 'Certifications', type: 'Information', scrollTo:'two-certificates' },
    { keywords: ['services', 'our', 'our services'], route: '/#services', title: 'Our Services', type: 'Information' },


  ];

  generalTerms.forEach(item => {
    if (item.keywords.some(keyword => keyword.includes(term) || term.includes(keyword))) {
      results.push({
        section: 'general',
        route: item.route,
        scrollTo: item.scrollTo,
        title: item.title,
        type: item.type,
        matchType: 'general'
      });
    }
  });

  // Remove duplicates based on route
  const uniqueResults = results.filter((result, index, self) =>
    index === self.findIndex((r) => r.route === result.route && r.title === result.title)
  );

  return uniqueResults.length > 0 ? uniqueResults : null;
};

/**
 * Get search suggestions based on partial input
 * @param {string} input - Partial search input
 * @param {Object} contentData - All content data
 * @returns {Array} - Array of suggestions
 */
export const getSearchSuggestions = (input, contentData) => {
  if (!input || input.length < 2) return [];
  
  const suggestions = new Set();
  
  // Add service titles
  contentData.Services?.forEach(service => {
    if (service.Title?.toLowerCase().startsWith(input.toLowerCase())) {
      suggestions.add(service.Title);
    }
  });
  
  // Add location names
  ['Copenhagen', 'Billund', 'Track Shipment'].forEach(loc => {
    if (loc.toLowerCase().includes(input.toLowerCase())) {
      suggestions.add(loc);
    }
  });
  
  // Add rate types
  ['Terminal Services', 'Material Price List', 'Terminal Fee'].forEach(rate => {
    if (rate.toLowerCase().includes(input.toLowerCase())) {
      suggestions.add(rate);
    }
  });
  
  return Array.from(suggestions).slice(0, 5);
};

export default searchContent;